import React, { InputHTMLAttributes, useEffect, useState, forwardRef, ReactNode } from 'react';
import InputFrame from '../atoms/InputFrame';

interface CurrencyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'prefix'> {
    label: string;
    errorMessage?: string;
    prefix?: ReactNode;
    onChange?: (value: number) => void;
    fractionDigits?: number;
    textColor?: string;
    maxValue?: number;
    readonly?: boolean;
    /** Troca a classe do `<input>` (o sublinhado do formulário) — para caber numa linha de filtro. */
    inputClassName?: string;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
    ({
        label,
        errorMessage,
        className,
        prefix,
        value = 0,
        onChange,
        fractionDigits = 0,
        textColor = "text-gray-900",
        disabled = false,
        readOnly = false,
        maxValue,
        id,
        inputClassName,
        ...restProps
    }, ref) => {

        const numberValue = Number(value);

        const [displayValue, setDisplayValue] = useState(formatCurrency(numberValue));
        const [previousValue, setPreviousValue] = useState(numberValue); // Armazena o valor anterior

        useEffect(() => {
            setDisplayValue(formatCurrency(numberValue));
            setPreviousValue(numberValue); // Atualiza o valor anterior quando 'value' muda
        }, [value]);

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let numericValue = e.target.value.replace(/[^0-9]/g, '');
            let parsedValue = Number(numericValue) / Math.pow(10, fractionDigits);


            if (maxValue !== undefined && parsedValue > maxValue) {
                // Se exceder o limite, reverte para o valor anterior
                setDisplayValue(formatCurrency(previousValue));
                if (onChange) {
                    onChange(previousValue);
                }
                return; // Impede a atualização do estado
            }

            const formattedValue = formatCurrency(parsedValue);

            setDisplayValue(formattedValue);
            setPreviousValue(parsedValue); // Atualiza o valor anterior
            if (onChange) {
                onChange(parsedValue);
            }
        };

        function formatCurrency(value: number): string {
            if (fractionDigits !== 2) {
                return value.toFixed(fractionDigits).replace('.', ',');
            }
            return value.toLocaleString('pt-BR', {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            });
        }

        let focusColor = "focus:border-blue-600 dark:focus:border-blue-400";

        if (errorMessage) {
            textColor = "text-red-700 dark:text-red-400";
            focusColor = "focus:border-red-600 dark:focus:border-red-400";

        }

        if (disabled) {
            textColor = "text-gray-500 cursor-not-allowed font-medium";
            focusColor = "";

        }

        // se className não conter algum 'mb-*' adiciona o 'mb-4' padrão
        if (!className || !/mb-\d+/.test(className)) {
            className = className ? `mb-4 ${className}` : 'mb-4';
        }

        return (
            <InputFrame
                label={label}
                htmlFor={id}
                errorMessage={errorMessage}
                prefix={prefix}
                className={className}
                textColor={textColor}
            >
                <input
                    type="text"
                    id={id}
                    ref={ref}
                    value={displayValue}
                    onChange={handleInputChange}
                    disabled={disabled || readOnly}
                    className={inputClassName ?? `block py-2.5 px-0 w-full text-sm ${textColor} bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 ${focusColor} peer dark:text-white`}
                    {...restProps}
                />
            </InputFrame>
        );
    }
);

CurrencyInput.displayName = 'CurrencyInput';

export default CurrencyInput;